/**
 * STAIR STUDIO (2026-10-09, Matt: "I want to see them notated for dimensions, riser/tread
 * counts, etc."). The ONE read of a stair's real numbers — the riser and tread the calculator
 * actually lands on, each flight's own rise and run, each run's width, each landing's depth —
 * and the code checks against them. The Studio's notes, its check list, and (stage 7) the
 * model's own stair notes all read this, so a note and a check can never disagree.
 *
 * Code values are the California Residential Code (IRC R311.7 / R312, the 2022 CRC carries the
 * same): checks WARN, never block (Matt's standing rule for the Studio). Comfort notes (the
 * 2R + T rule, a riser over 7-1/4") are advice, not code.
 */
import type { Path2D } from './types'
import { stairRun, stairStepInputs, stairVerticalExtent, type StairRun } from './stair'
import { stairLayoutForPath, type StairLayout, stairLegEnd } from './stairLayout'
import { stairConstructionOf } from './stairStructures'

export interface StairFlight {
  /** the leg index in the layout */
  leg: number
  risers: number
  treads: number
  /** this flight's rise and horizontal run (feet) */
  riseFt: number
  runFt: number
  widthFt: number
}

export interface StairFacts {
  zBase: number
  zTop: number
  riseFt: number
  calc: StairRun
  treadFt: number
  nosingFt: number
  widthFt: number
  flights: StairFlight[]
  /** landing depth in the direction of travel (feet) where the layout knows it */
  landings: { depthFt: number | null; widthFt: number }[]
  layout: StairLayout | null
  sketched: boolean
  /** STAIR STUDIO stage 3: a spiral / helical stair's curve */
  curve: import('./stairCurve').StairCurve | null
  /** STAIR STUDIO stage 2: the construction's open risers and tread thickness (feet) */
  openRisers: boolean
  treadT: number
  /** STAIR STUDIO stage 4: a bar across each open riser */
  riserBar: boolean
}

/** the numbers a stair actually builds to (null when it can't lay out) */
export function stairFactsOf(el: {
  level: { elevationFt: number } | null
  params: Record<string, unknown>
  typeParams?: Record<string, unknown>
  paramLevels?: Record<string, { elevationFt: number }>
}): StairFacts | null {
  const extent = stairVerticalExtent(el)
  if (!extent) return null
  const merged = { ...(el.typeParams ?? {}), ...el.params }
  const { maxRiserFt, treadDepthFt } = stairStepInputs(merged, extent.zTop - extent.zBase)
  const riseFt = Math.max(extent.zTop - extent.zBase, maxRiserFt)
  const widthFt = (el.params.widthFt as number) || 3.5
  const nosingFt = typeof el.params.nosingFt === 'number' ? el.params.nosingFt : 1 / 12
  const sketched = !!el.params.sketch
  const path = el.params.path as Path2D | null
  const layout = !sketched && path ? stairLayoutForPath(path, { widthFt, floorToFloorFt: riseFt, maxRiserFt, treadDepthFt }, el.params) : null
  const calc = layout?.calc ?? stairRun(riseFt, maxRiserFt, treadDepthFt)
  const flights: StairFlight[] = []
  if (layout) {
    for (let i = 0; i < layout.legs.length; i++) {
      const g = layout.legs[i]
      const treads = g.kEnd - g.kStart
      const nextZ = stairLegEnd(layout, i, riseFt).zEnd
      const r = Math.max(0, nextZ - g.z0)
      // STAIR BUILDER: winders don't end a flight (only a landing does) — a run that climbs on
      // through winders (more than one riser to the next run) joins the next run's flight
      const prev = flights[flights.length - 1]
      const prevG = i > 0 ? layout.legs[i - 1] : null
      const joined = !!(prev && prevG && layout.winderZones?.length && g.z0 - (prevG.z0 + (prevG.kEnd - prevG.kStart) * calc.riserFt) > 1.5 * calc.riserFt)
      if (joined) {
        prev.riseFt += r; prev.risers = Math.round(prev.riseFt / calc.riserFt); prev.treads += treads; prev.runFt += treads * treadDepthFt
      } else flights.push({ leg: i, risers: Math.round(r / calc.riserFt), treads, riseFt: r, runFt: treads * treadDepthFt, widthFt: g.w || widthFt })
    }
  }
  if (!flights.length && layout?.sweep?.landingK.length) {
    // a swept stair with landings: a flight from each floor or landing to the next
    let from = 0
    for (const k of [...layout.sweep.landingK, calc.treads]) {
      const risers = k + 1 - from
      flights.push({ leg: flights.length, risers, treads: Math.max(0, risers - 1), riseFt: risers * calc.riserFt, runFt: Math.max(0, risers - 1) * treadDepthFt, widthFt })
      from = k + 1
    }
  }
  if (!flights.length) flights.push({ leg: 0, risers: calc.risers, treads: calc.treads, riseFt, runFt: calc.runFt, widthFt })
  const landings = (layout?.landings ?? []).map((l) => ({ depthFt: typeof l.depth === 'number' ? l.depth : null, widthFt }))
  const k = stairConstructionOf(el.params)
  const curve = layout?.curve ?? null
  const built = k.structure.value !== 'solid' && k.structure.value !== 'concrete'
  return { zBase: extent.zBase, zTop: extent.zTop, riseFt, calc, treadFt: curve ? curve.rWalk * curve.dTheta : treadDepthFt, nosingFt: built && k.openRisers ? 0 : nosingFt, widthFt, flights, landings, layout, sketched, openRisers: built && k.openRisers, treadT: built ? k.treadT : 0, curve, riserBar: built && k.openRisers && el.params.riserBar === true }
}

export type StairCheckLevel = 'ok' | 'fail' | 'note'
export interface StairCheck {
  key: string
  label: string
  /** what the stair has, written for a person ("7-1/2\"") */
  value: string
  /** the rule, written for a person */
  rule: string
  level: StairCheckLevel
}

const IN = 1 / 12
export const STAIR_CODE = {
  maxRiserFt: 7.75 * IN,
  minTreadFt: 10 * IN,
  minWidthFt: 36 * IN,
  maxFlightRiseFt: 151 * IN,
  nosingMinFt: 0.75 * IN,
  nosingMaxFt: 1.25 * IN,
  /** a tread this deep needs no nosing */
  noNosingTreadFt: 11 * IN,
  minHeadroomFt: 80 * IN,
  minLandingFt: 36 * IN,
  comfortRiserFt: 7.25 * IN,
}

/** inches as a person writes them: 7-1/2", 11", 3'-0" over a foot */
export function fmtStairIn(ft: number): string {
  const totalIn = ft * 12
  const sixteenths = Math.round(totalIn * 16)
  const feet = totalIn >= 12 ? Math.floor(sixteenths / 192) : 0
  let rem = sixteenths - feet * 192
  const whole = Math.floor(rem / 16)
  rem -= whole * 16
  let frac = ''
  if (rem) {
    let n = rem, d = 16
    while (n % 2 === 0) { n /= 2; d /= 2 }
    frac = `${n}/${d}`
  }
  const inch = whole && frac ? `${whole}-${frac}` : frac || String(whole)
  return feet ? `${feet}'-${inch}"` : `${inch}"`
}

/** inches only, as risers, treads and nosings are written (12", never 1'-0") */
export function fmtStairInches(ft: number): string {
  const sixteenths = Math.round(ft * 12 * 16)
  const whole = Math.floor(sixteenths / 16)
  let rem = sixteenths - whole * 16
  let frac = ''
  if (rem) {
    let d = 16
    while (rem % 2 === 0) { rem /= 2; d /= 2 }
    frac = `${rem}/${d}`
  }
  return (whole && frac ? `${whole}-${frac}` : frac || String(whole)) + '"'
}

/** the code checks for a stair's facts (Matt: warn, never block) */
export function stairChecks(f: StairFacts): StairCheck[] {
  if (f.curve) return curvedStairChecks(f, f.curve)
  const out: StairCheck[] = []
  const r = f.calc.riserFt, t = f.treadFt
  out.push({ key: 'riser', label: 'Riser height', value: fmtStairInches(r), rule: `7-3/4" max`, level: r <= STAIR_CODE.maxRiserFt + 1e-6 ? 'ok' : 'fail' })
  out.push({ key: 'tread', label: 'Tread depth', value: fmtStairInches(t), rule: `10" min`, level: t >= STAIR_CODE.minTreadFt - 1e-6 ? 'ok' : 'fail' })
  const minW = Math.min(f.widthFt, ...f.flights.map((x) => x.widthFt))
  out.push({ key: 'width', label: 'Width', value: fmtStairIn(minW), rule: `36" min`, level: minW >= STAIR_CODE.minWidthFt - 1e-6 ? 'ok' : 'fail' })
  // STAIR BUILDER: winders (CRC R311.7.5.2.1) — at the walk line, 12" from the narrow end, and
  // at the narrowest point (the builder sizes each turn to pass; an override shows here)
  // the curved family: every tread is tapered, so the winder rule applies the whole way
  const sw = f.layout?.sweep
  if (sw) {
    out.push({ key: 'sweepWalk', label: 'Treads at the walk line', value: fmtStairInches(sw.walkMinFt), rule: `10" min, 12" from the narrow end`, level: sw.walkMinFt >= STAIR_CODE.minTreadFt - 1e-6 ? 'ok' : 'fail' })
    out.push({ key: 'sweepNarrow', label: 'Treads at the narrow end', value: fmtStairInches(sw.narrowFt), rule: `6" min`, level: sw.narrowFt >= 0.5 - 1e-6 ? 'ok' : 'fail' })
  }
  for (const [i, z] of (f.layout?.winderZones ?? []).entries()) {
    const tag = (f.layout!.winderZones!.length > 1 ? ` ${i + 1}` : '')
    out.push({ key: 'winderWalk' + i, label: `Winders${tag} at the walk line`, value: fmtStairInches(z.walkDepthFt), rule: `10" min, 12" from the narrow end`, level: z.walkDepthFt >= STAIR_CODE.minTreadFt - 1e-6 ? 'ok' : 'fail' })
    out.push({ key: 'winderNarrow' + i, label: `Winders${tag} at the narrow end`, value: fmtStairInches(z.narrowFt), rule: `6" min`, level: z.narrowFt >= 0.5 - 1e-6 ? 'ok' : 'fail' })
  }
  if (t < STAIR_CODE.noNosingTreadFt - 1e-6) {
    const n = f.nosingFt
    out.push({
      key: 'nosing', label: 'Nosing', value: fmtStairInches(n), rule: `3/4" to 1-1/4" (treads under 11")`,
      level: n >= STAIR_CODE.nosingMinFt - 1e-6 && n <= STAIR_CODE.nosingMaxFt + 1e-6 ? 'ok' : 'fail',
    })
  } else if (f.nosingFt > STAIR_CODE.nosingMaxFt + 1e-6) {
    out.push({ key: 'nosing', label: 'Nosing', value: fmtStairInches(f.nosingFt), rule: `1-1/4" max`, level: 'fail' })
  }
  const tallest = Math.max(...f.flights.map((x) => x.riseFt))
  out.push({ key: 'flight', label: 'Rise of one flight', value: fmtStairIn(tallest), rule: `12'-7" max between landings`, level: tallest <= STAIR_CODE.maxFlightRiseFt + 1e-6 ? 'ok' : 'fail' })
  for (let i = 0; i < f.landings.length; i++) {
    const l = f.landings[i]
    if (l.depthFt == null) continue
    const need = Math.max(STAIR_CODE.minLandingFt, minW)
    out.push({ key: 'landing' + i, label: f.landings.length > 1 ? `Landing ${i + 1} depth` : 'Landing depth', value: fmtStairIn(l.depthFt), rule: `at least the stair width, 36" min`, level: l.depthFt >= need - 1e-6 ? 'ok' : 'fail' })
  }
  if (f.openRisers && f.riseFt > 30 / 12) {
    // an open riser: the gap between treads must not pass a 4" sphere once the stair rises 30"
    const gap = f.riserBar ? Math.max(0, (r - f.treadT - 0.5 / 12) / 2) : Math.max(0, r - f.treadT)
    out.push({ key: 'openRiser', label: 'Open riser gap', value: fmtStairInches(gap), rule: `4" sphere max (add a riser bar or a deeper tread)`, level: gap <= 4 / 12 + 1e-6 ? 'ok' : 'fail' })
  }
  const twoRT = (2 * r + t) * 12
  out.push({
    key: 'comfort', label: 'Comfort (2R + T)', value: `${twoRT.toFixed(1)}"`, rule: `24" to 25-1/2" walks best`,
    level: twoRT >= 24 - 1e-6 && twoRT <= 25.5 + 1e-6 ? 'ok' : 'note',
  })
  return out
}

/**
 * STAIR STUDIO stage 3: the checks for a curved stair. Spiral (CRC R311.7.10.1): 26" clear,
 * 6-3/4" tread at the walk line (12" out from the narrow edge), 9-1/2" riser, 6'-6" headroom.
 * Helical / curved: 11" tread at the walk line, 6" at the narrow end, the inner radius at least
 * twice the width, the ordinary riser and width.
 */
function curvedStairChecks(f: StairFacts, c: import('./stairCurve').StairCurve): StairCheck[] {
  const out: StairCheck[] = []
  const r = f.calc.riserFt
  const walk = c.rWalk * c.dTheta, narrow = c.rIn * c.dTheta
  const width = c.rOut - c.rIn
  if (c.kind === 'spiral') {
    out.push({ key: 'riser', label: 'Riser height', value: fmtStairInches(r), rule: `9-1/2" max (spiral)`, level: r <= 9.5 * IN + 1e-6 ? 'ok' : 'fail' })
    out.push({ key: 'tread', label: 'Tread at the walk line', value: fmtStairInches(walk), rule: `6-3/4" min, 12" from the column`, level: walk >= 6.75 * IN - 1e-6 ? 'ok' : 'fail' })
    out.push({ key: 'width', label: 'Clear width', value: fmtStairIn(width), rule: `26" min (spiral)`, level: width >= 26 * IN - 1e-6 ? 'ok' : 'fail' })
    out.push({ key: 'headroom', label: 'Headroom (one turn)', value: fmtStairIn(((2 * Math.PI) / c.dTheta) * r - f.treadT), rule: `6'-6" min (spiral)`, level: ((2 * Math.PI) / c.dTheta) * r - f.treadT >= 78 * IN - 1e-6 ? 'ok' : 'fail' })
  } else {
    out.push({ key: 'riser', label: 'Riser height', value: fmtStairInches(r), rule: `7-3/4" max`, level: r <= STAIR_CODE.maxRiserFt + 1e-6 ? 'ok' : 'fail' })
    out.push({ key: 'tread', label: 'Tread at the walk line', value: fmtStairInches(walk), rule: `11" min, 12" from the inner edge`, level: walk >= 11 * IN - 1e-6 ? 'ok' : 'fail' })
    out.push({ key: 'narrow', label: 'Tread at the narrow end', value: fmtStairInches(narrow), rule: `6" min`, level: narrow >= 6 * IN - 1e-6 ? 'ok' : 'fail' })
    out.push({ key: 'width', label: 'Width', value: fmtStairIn(width), rule: `36" min`, level: width >= STAIR_CODE.minWidthFt - 1e-6 ? 'ok' : 'fail' })
    out.push({ key: 'radius', label: 'Inner radius', value: fmtStairIn(c.rIn), rule: `at least twice the width`, level: c.rIn >= 2 * width - 1e-6 ? 'ok' : 'fail' })
  }
  if (f.openRisers && f.riseFt > 30 / 12) {
    const gap = f.riserBar ? Math.max(0, (r - f.treadT - 0.5 / 12) / 2) : Math.max(0, r - f.treadT)
    out.push({ key: 'openRiser', label: 'Open riser gap', value: fmtStairInches(gap), rule: `4" sphere max`, level: gap <= 4 / 12 + 1e-6 ? 'ok' : 'fail' })
  }
  return out
}

